import type { Translations } from '../types';

export const ja: Translations = {
  common: {
    home: 'ホーム',
    github: 'GitHub',
    copy: 'コピー',
    clear: 'クリア',
    input: '入力',
    output: '出力',
    encode: 'エンコード',
    decode: 'デコード',
    generate: '生成',
    convert: '変換',
    result: '結果',
    copiedToClipboard: 'クリップボードにコピーしました！',
    failedToCopy: 'コピーに失敗しました',
    language: '言語',
  },
  seo: {
    siteDescription: '無料でプライバシーに配慮した開発者ツールコレクション。JSONフォーマッター、Base64エンコーダー/デコーダー、UUID生成、ハッシュ生成、URLエンコーダー、タイムスタンプ変換、カラーコンバーター。すべてのツールはブラウザでオフラインで動作します。',
    keywords: [
      '開発者ツール',
      'JSONフォーマッター',
      'Base64エンコーダー',
      'UUID生成',
      'ハッシュ生成',
      'URLエンコーダー',
      'タイムスタンプ変換',
      'カラーコンバーター',
      'オンラインツール',
      '無料ツール',
      'プライバシー重視',
      'オフラインツール',
    ],
    ogTitle: 'GetDev.Tools - 無料のプライバシー重視開発者ツール',
    ogDescription: 'ブラウザでオフライン動作する無料の開発者ツールコレクション。JSONフォーマッター、Base64、UUID、ハッシュ、URLエンコーダーなど。',
  },
  home: {
    title: '開発者ツール集',
    subtitle: '無料でプライバシー重視の開発者ツール。すべての処理はブラウザ内で行われます。',
    whyTitle: 'GetDev.Toolsを選ぶ理由',
    privacyTitle: '🔒 プライバシー第一',
    privacyDesc: 'すべての処理はブラウザ内で行われます。データがデバイスから外部に送信されることはありません。',
    fastTitle: '⚡ 超高速',
    fastDesc: 'サーバーとの通信なし。すべてローカルで実行され、即座に結果が得られます。',
    freeTitle: '🆓 永久無料',
    freeDesc: 'オープンソースで無料。登録や支払いは不要です。',
    footer: '© 2025 GetDev.Tools - プライバシー重視の開発者ツール',
    footerSubtitle: 'すべての処理はブラウザ内で行われます',
  },
  tools: {
    jsonFormatter: {
      name: 'JSONフォーマッター',
      description: 'JSONデータのフォーマット、検証、整形',
      format: 'フォーマット',
      minify: '圧縮',
      validate: '検証',
      error: 'エラー',
    },
    base64: {
      name: 'Base64エンコーダー/デコーダー',
      description: 'Base64文字列のエンコードとデコード',
      encodeButton: 'エンコード',
      decodeButton: 'デコード',
    },
    uuid: {
      name: 'UUID生成器',
      description: 'RFC 4122準拠のUUID v4識別子を生成',
      numberOfUuids: '生成するUUID数',
      generatedUuids: '生成されたUUID',
      copyAll: 'すべてコピー',
      aboutTitle: 'UUID v4について',
      aboutDesc: 'UUID（汎用一意識別子）v4はランダムに生成される128ビットの識別子です。衝突の確率は極めて低く、中央管理なしの分散システムに適しています。',
    },
    hash: {
      name: 'ハッシュ生成器',
      description: 'SHA-1、SHA-256、SHA-512ハッシュを生成',
      inputText: '入力テキスト',
      securityNotice: '⚠️ セキュリティ通知',
      securityDesc: 'SHA-1はセキュリティ上の理由で非推奨です。暗号化アプリケーションにはSHA-256またはSHA-512を使用してください。MD5はセキュリティの脆弱性により、現代のブラウザでは利用できません。',
    },
    urlEncoder: {
      name: 'URLエンコーダー/デコーダー',
      description: 'URL文字列のエンコードとデコード',
      encodeUrl: 'URLエンコード',
      encodeComponent: 'コンポーネントエンコード',
      encoded: 'エンコード済み',
      decoded: 'デコード済み',
      differenceTitle: '💡 違い',
      encodeUriDesc: 'encodeURI: 完全なURIをエンコード（エンコードしない: ; , / ? : @ & = + $ #）',
      encodeComponentDesc: 'encodeURIComponent: URIコンポーネントをエンコード（すべての特殊文字をエンコード）',
      examples: '例',
    },
    timestamp: {
      name: 'タイムスタンプ変換器',
      description: 'Unixタイムスタンプと人間が読める日付の間で変換',
      currentTime: '現在時刻',
      unixMs: 'Unixタイムスタンプ（ミリ秒）',
      unixS: 'Unixタイムスタンプ（秒）',
      toDate: 'タイムスタンプ → 日付',
      toTimestamp: '日付 → タイムスタンプ',
      useNow: '現在時刻を使用',
      aboutTitle: '💡 Unixタイムスタンプについて',
      aboutDesc1: '• Unixタイムスタンプ = 1970年1月1日00:00:00 UTC（エポック）からの秒数',
      aboutDesc2: '• 10桁 = 秒（例：1609459200）',
      aboutDesc3: '• 13桁 = ミリ秒（例：1609459200000）',
      aboutDesc4: '• サポートされる日付形式：ISO 8601、RFC 2822、または自然言語',
    },
    color: {
      name: 'カラー変換器',
      description: 'HEX、RGB、HSL形式間で変換',
      pickColor: '色を選択',
      formatGuide: '💡 カラーフォーマットガイド',
      hexDesc: 'HEX: 16進表記（#RRGGBB）',
      rgbDesc: 'RGB: 赤、緑、青の値（0-255）',
      hslDesc: 'HSL: 色相（0-360°）、彩度（0-100%）、明度（0-100%）',
      alphaDesc: 'RGBA/HSLA: RGB/HSLと同じで、アルファ（不透明度）チャンネル付き',
    },
    jwt: {
      name: 'JWTデコーダー',
      description: 'JSON Web Tokenのデコードと検査',
      inputToken: 'JWTトークン',
      header: 'ヘッダー',
      payload: 'ペイロード',
      signature: '署名 (Base64)',
      claims: '標準クレーム',
      invalidFormat: 'JWT形式が無効です。トークンはドットで区切られた3つの部分で構成されている必要があります。',
      invalidToken: 'JWTトークンが無効です',
      securityNotice: 'セキュリティ通知',
      securityDesc: 'このツールはJWTトークンをデコードするのみです。署名は検証しません。本番システムの機密トークンを貼り付けないでください。',
      aboutTitle: '💡 JWTについて',
      aboutDesc1: 'JWT = JSON Web Token、セキュアな認証に使用',
      aboutDesc2: '形式：ヘッダー.ペイロード.署名（Base64エンコード）',
      aboutDesc3: 'ヘッダーにはアルゴリズムとトークンタイプ、ペイロードにはクレームが含まれる',
    },
    diff: {
      name: 'テキスト比較',
      description: '2つのテキストブロックを比較して違いを表示',
      originalText: '元のテキスト',
      modifiedText: '変更後のテキスト',
      sideBySide: '並べて表示',
      unified: '統合表示',
      noDifference: '違いが見つかりませんでした',
    },
    markdown: {
      name: 'Markdownプレビュー',
      description: 'Markdownをリアルタイムで記述およびプレビュー',
      editor: 'エディター',
      preview: 'プレビュー',
      supportedFeatures: 'サポート：見出し、リスト、リンク、画像、コードブロック、表など',
    },
    regex: {
      name: '正規表現テスター',
      description: '正規表現をテストして一致を確認',
      pattern: '正規表現',
      flags: 'フラグ',
      testString: 'テスト文字列',
      matches: '一致',
      noMatches: '一致が見つかりませんでした',
      invalidRegex: '正規表現が無効です',
      globalFlag: 'グローバル (g)',
      caseInsensitive: '大文字小文字を区別しない (i)',
      multiline: '複数行 (m)',
      dotAll: 'ドットが改行に一致 (s)',
    },
    qrCode: {
      name: 'QRコードジェネレーター',
      description: 'URL、テキスト、WiFiなどのQRコードを生成',
      inputData: '入力データ',
      qrCodeImage: 'QRコード画像',
      size: 'サイズ (ピクセル)',
      errorCorrection: '誤り訂正レベル',
      errorLow: '低 (~7%)',
      errorMedium: '中 (~15%)',
      errorQuartile: '高 (~25%)',
      errorHigh: '最高 (~30%)',
      templates: 'クイックテンプレート',
      templateUrl: 'ウェブサイトURL',
      templateEmail: 'メール',
      templatePhone: '電話番号',
      templateSms: 'SMS',
      templateWifi: 'WiFiネットワーク',
      templateVcard: '連絡先カード (vCard)',
      privacyNotice: '🔒 プライバシーに関する注意',
      privacyDesc: 'QRコードは外部API (qrserver.com) を使用して生成されます。データはそのサーバーに送信されます。',
    },
    lorem: {
      name: 'Lorem Ipsumジェネレーター',
      description: 'デザインとモックアップ用のプレースホルダーテキストを生成',
      generateType: '生成タイプ',
      paragraphs: '段落',
      sentences: '文',
      words: '単語',
      count: '数量',
      startWithLorem: '"Lorem ipsum"で始める',
      generatedText: '生成されたテキスト',
      aboutTitle: '💡 Lorem Ipsumについて',
      aboutDesc: 'Lorem Ipsumはデザインや出版で一般的に使用されるプレースホルダーテキストです。英語の文字頻度に近似しています。',
    },
    htmlEntity: {
      name: 'HTMLエンティティエンコーダー',
      description: 'HTMLエンティティのエンコードとデコード',
      inputText: '入力テキスト',
      encodeBasic: 'エンコード（基本）',
      encodeAll: 'すべてエンコード',
      decode: 'デコード',
      commonEntities: '一般的なHTMLエンティティ',
      character: '文字',
      entity: 'エンティティ',
      description2: '説明',
      useCases: '💡 使用例',
      useCase1: '基本エンコード：重要な文字のみをエンコード (<, >, &, ", \')',
      useCase2: 'すべてエンコード：すべての特殊文字をエンコードして最大の安全性を確保',
      useCase3: 'デコード：HTMLエンティティを通常の文字に戻す',
    },
  },
};

